import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Crown, Sparkle, Fire, UsersThree, X } from "@phosphor-icons/react";

const ICON = {
  crown: <Crown size={16} weight="fill" className="text-[#C8A74B]" />,
  sparkle: <Sparkle size={16} weight="fill" className="text-secondary" />,
  fire: <Fire size={16} weight="fill" className="text-[#E15F3F]" />,
  users: <UsersThree size={16} weight="fill" className="text-primary" />,
};

/**
 * SocialTicker — rotating social proof snippets, bottom-right, dismissible.
 *
 * Live feed of real signal (premium activations / trials / contact intents /
 * claims) with light anonymization. Dedup'd per day so a returning visitor
 * doesn't see it again until tomorrow.
 */
export default function SocialTicker() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Dismissed today? Don't show.
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem("mc68_ticker_dismissed") === today) return;

    let stop = false;
    const fetchTicker = async () => {
      try {
        const r = await api.get("/public/social-ticker", { params: { limit: 8 } });
        if (stop) return;
        const list = r?.data?.items || [];
        if (list.length === 0) return;
        setItems(list);
        setTimeout(() => !stop && setOpen(true), 2200);
      } catch (err) {
        console.debug("[SocialTicker] fetch failed", err);
      }
    };
    fetchTicker();
    return () => { stop = true; };
  }, []);

  // Rotate every 4.5s
  useEffect(() => {
    if (!open || items.length <= 1) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 4500);
    return () => clearInterval(id);
  }, [open, items.length]);

  if (!open || items.length === 0) return null;
  const cur = items[idx];

  const dismiss = () => {
    setOpen(false);
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem("mc68_ticker_dismissed", today);
    } catch { /* noop */ }
  };

  return (
    <Link
      to="/passez-partenaire"
      data-testid="social-ticker"
      className="fixed bottom-5 right-5 z-[60] group animate-in slide-in-from-bottom-5 fade-in duration-500"
      aria-label="Preuve sociale — Mon Commerce 68"
    >
      <div className="flex items-center gap-3 bg-card ring-1 ring-border shadow-xl rounded-full pl-4 pr-3 py-2.5 hover:ring-primary/40 transition-all max-w-sm">
        <span className="shrink-0 w-6 h-6 rounded-full bg-muted grid place-items-center">
          {ICON[cur.icon] || ICON.users}
        </span>
        <span className="text-[13px] leading-snug text-foreground truncate" data-testid="social-ticker-text">
          {cur.text}
        </span>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(); }}
          className="shrink-0 w-5 h-5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted grid place-items-center opacity-60 group-hover:opacity-100 transition"
          aria-label="Masquer"
          data-testid="social-ticker-close"
        >
          <X size={11} />
        </button>
      </div>
    </Link>
  );
}
